<template>
  <div>
    <div class="page-breadcrumb border-bottom">
      <div class="row">
        <div class="col-lg-6 col-md-6 col-xs-12 align-self-center">
          <h5 class="mb-0 font-medium text-uppercase">Template packs</h5>
        </div>
        <div class="col-lg-6 col-md-6 col-xs-12 align-self-center">
          <nav aria-label="breadcrumb" class="float-left mt-2 float-md-right">
            <ol class="p-0 mb-0 breadcrumb justify-content-end">
              <li class="breadcrumb-item"><a>Home</a></li>
              <li class="breadcrumb-item active" aria-current="page">Template packs</li>
            </ol>
          </nav>
        </div>
      </div>
    </div>

    <div class="page-content container-fluid">
      <section class="packs-card">
        <header class="packs-header">
          <p class="packs-muted">
            A pack is one folder in the template account holding a deck, its surveys and one course.
            Publishing it makes the folder available as a template.
          </p>
          <aha-button variant="primary" @click="openPublishModal()">
            <span slot="icon"><aha-icon name="system-plus" size="16" decorative></aha-icon></span>
            Publish pack
          </aha-button>
        </header>

        <p v-if="isLoading" class="packs-muted" role="status">Loading template packs</p>
        <aha-alert v-else-if="loadError" type="error">
          {{ loadError }}
          <aha-button slot="action" size="sm" @click="loadPacks">Try again</aha-button>
        </aha-alert>
        <aha-empty
          v-else-if="!packs.length"
          description="No template packs published yet. Use Publish pack to publish the first one."
        ></aha-empty>
        <div v-else class="packs-table-wrap">
          <table class="packs-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Slug</th>
                <th>Deck</th>
                <th>Files</th>
                <th>State</th>
                <th>Published</th>
                <th><span class="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pack in packs" :key="pack.folder_id">
                <td class="packs-strong">{{ pack.title }}</td>
                <td>{{ pack.slug }}</td>
                <td>{{ pack.deck ? pack.deck.name : '-' }}</td>
                <td>{{ pack.files.length }}</td>
                <td>
                  <aha-status-badge v-bind="badgeAttributes(pack.state)">{{ pack.state }}</aha-status-badge>
                </td>
                <td>{{ formatDate(pack.published_at) }}</td>
                <td>
                  <div class="packs-actions">
                    <a
                      v-if="pack.course_url"
                      class="packs-link"
                      :href="pack.course_url"
                      target="_blank"
                      rel="noopener"
                    >Open course page</a>
                    <aha-button size="sm" @click="openPublishModal(pack)">Re-publish</aha-button>
                    <aha-button size="sm" variant="danger" @click="confirmUnpublish(pack)">Unpublish</aha-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <b-modal id="publish-pack-modal" ref="publishModal" hide-footer title="Publish pack">
      <form class="packs-form" novalidate @submit.prevent="publish">
        <div class="packs-field">
          <span class="packs-label">Folder id</span>
          <div class="packs-inline">
            <aha-input
              aria-label="Folder id"
              type="number"
              placeholder="Master folder id in the template account"
              :value="form.folderId"
              @input="onFolderIdInput($event)"
            ></aha-input>
            <aha-button
              variant="secondary"
              :disabled="!form.folderId || isChecking"
              :loading="isChecking"
              @click="check"
            >Check folder</aha-button>
          </div>
        </div>

        <aha-alert v-if="checkError" type="error">{{ checkError }}</aha-alert>

        <template v-if="previewData">
          <ul class="packs-summary">
            <li><strong>Folder:</strong> {{ previewData.folder.name }}</li>
            <li><strong>Deck:</strong> {{ previewData.deck ? previewData.deck.name : 'None found' }}</li>
            <li><strong>Course:</strong> {{ previewData.course ? previewData.course.title : 'None found' }}</li>
            <li>
              <strong>Surveys ({{ previewData.surveys.length }}):</strong>
              {{ previewData.surveys.map((survey) => survey.title).join(', ') || 'None' }}
            </li>
          </ul>
          <aha-alert v-if="previewData.problems.length" type="warning" heading="This folder cannot be published yet">
            <ul class="packs-problems">
              <li v-for="problem in previewData.problems" :key="problem">{{ problem }}</li>
            </ul>
          </aha-alert>
          <template v-else>
            <div class="packs-field">
              <span class="packs-label">Slug</span>
              <aha-input
                aria-label="Slug"
                :value="form.slug"
                @input="form.slug = detailValue($event, form.slug)"
              ></aha-input>
            </div>
            <div class="packs-field">
              <span class="packs-label">Title</span>
              <aha-input
                aria-label="Title"
                maxlength="100"
                :value="form.title"
                @input="form.title = detailValue($event, form.title)"
              ></aha-input>
            </div>
          </template>
        </template>

        <aha-alert v-if="publishError" type="error">{{ publishError }}</aha-alert>

        <div class="packs-footer">
          <aha-button @click="closePublishModal">Cancel</aha-button>
          <aha-button variant="primary" :disabled="!canPublish" :loading="isPublishing" @click="publish">
            Publish
          </aha-button>
        </div>
      </form>
    </b-modal>

    <b-modal id="unpublish-pack-modal" ref="unpublishModal" hide-footer title="Unpublish pack">
      <p v-if="packToUnpublish">
        Unpublish <strong>{{ packToUnpublish.title }}</strong>? It stops being available as a template.
        The folder and its files are kept.
      </p>
      <div class="packs-footer">
        <aha-button @click="$refs.unpublishModal.hide()">Cancel</aha-button>
        <aha-button variant="danger" :loading="isUnpublishing" @click="unpublish">Unpublish pack</aha-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import TemplatePacksApi from '@/api/template-packs'
import utils from '@/utils'
import { withErrorRef } from '@/apiErrorRef'
import nativeAhaElements from '@/views/apps/stripe-bridge/nativeAhaElements'

const KNOWN_BADGE_STATES = ['draft', 'scheduled', 'published', 'live', 'active', 'closed', 'inactive', 'archived']

const errorMessage = (error, fallback) => {
  const body = error && error.body
  return (body && body.message) || withErrorRef(error, fallback)
}

export default {
  name: 'TemplatePacks',
  components: { ...nativeAhaElements },
  data() {
    return {
      packs: [],
      isLoading: false,
      loadError: '',
      form: { folderId: '', slug: '', title: '' },
      previewData: null,
      isChecking: false,
      checkError: '',
      isPublishing: false,
      publishError: '',
      packToUnpublish: null,
      isUnpublishing: false,
    }
  },
  computed: {
    canPublish() {
      return !!this.previewData
        && !this.previewData.problems.length
        && !!this.form.slug
        && !!this.form.title
        && !this.isPublishing
    },
  },
  created() {
    this.loadPacks()
  },
  methods: {
    detailValue(event, fallback) {
      return event && event.detail && typeof event.detail.value === 'string' ? event.detail.value : fallback
    },
    badgeAttributes(state) {
      return KNOWN_BADGE_STATES.includes(state) ? { status: state } : { look: 'quiet' }
    },
    formatDate(value) {
      return value ? new Date(value).toLocaleString('en-GB') : '-'
    },
    async loadPacks() {
      this.isLoading = true
      this.loadError = ''
      try {
        this.packs = await TemplatePacksApi.list()
      } catch (error) {
        this.loadError = errorMessage(error, 'Could not load the template packs. Try again.')
      } finally {
        this.isLoading = false
      }
    },
    resetCheck() {
      this.previewData = null
      this.checkError = ''
      this.publishError = ''
    },
    onFolderIdInput(event) {
      this.form.folderId = this.detailValue(event, this.form.folderId)
      this.resetCheck()
    },
    openPublishModal(pack) {
      this.resetCheck()
      this.form = { folderId: pack ? String(pack.folder_id) : '', slug: '', title: '' }
      this.$refs.publishModal.show()
      if (pack) this.check()
    },
    closePublishModal() {
      this.$refs.publishModal.hide()
    },
    async check() {
      this.resetCheck()
      this.isChecking = true
      const requestedFolderId = this.form.folderId
      try {
        const data = await TemplatePacksApi.preview(requestedFolderId)
        if (requestedFolderId !== this.form.folderId) return
        this.previewData = data
        this.form.slug = data.suggested_slug || ''
        this.form.title = (data.folder.name || '').slice(0, 100)
      } catch (error) {
        this.checkError = errorMessage(error, 'Could not check this folder. Try again.')
      } finally {
        this.isChecking = false
      }
    },
    async publish() {
      if (!this.canPublish) return
      this.isPublishing = true
      this.publishError = ''
      try {
        await TemplatePacksApi.publish({
          folderId: Number(this.form.folderId),
          slug: this.form.slug,
          title: this.form.title,
        })
        this.closePublishModal()
        utils.showToastSuccess('Pack published')
        this.loadPacks()
      } catch (error) {
        const problems = error && error.body && error.body.problems
        this.publishError = problems && problems.length
          ? problems.join('. ')
          : errorMessage(error, 'Could not publish this pack. Try again.')
      } finally {
        this.isPublishing = false
      }
    },
    confirmUnpublish(pack) {
      this.packToUnpublish = pack
      this.$refs.unpublishModal.show()
    },
    async unpublish() {
      this.isUnpublishing = true
      try {
        await TemplatePacksApi.unpublish(this.packToUnpublish.folder_id)
        this.$refs.unpublishModal.hide()
        utils.showToastSuccess('Pack unpublished')
        this.loadPacks()
      } catch (error) {
        utils.showToastError(errorMessage(error, 'Could not unpublish this pack. Try again.'))
      } finally {
        this.isUnpublishing = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.packs-card {
  background: var(--aha-bg-container);
  border: 1px solid var(--aha-border-default);
  border-radius: var(--aha-radius-default);
  padding: var(--aha-space-24);
  font-family: var(--aha-font-product);
  color: var(--aha-text-default);
}

.packs-header {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--aha-space-16);
  margin-bottom: var(--aha-space-16);
}

.packs-muted {
  margin: 0;
  max-width: 640px;
  color: var(--aha-text-secondary);
}

.packs-table-wrap {
  overflow-x: auto;
}

.packs-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--aha-size-default);

  th {
    text-align: left;
    font-size: var(--aha-size-sm);
    font-weight: 600;
    color: var(--aha-text-secondary);
  }

  th,
  td {
    padding: var(--aha-space-8) var(--aha-space-12);
    border-bottom: 1px solid var(--aha-border-default);
    vertical-align: middle;
  }
}

.packs-strong {
  font-weight: 600;
}

.packs-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: var(--aha-space-8);
}

.packs-link {
  color: var(--aha-text-link);
  font-size: var(--aha-size-default);
}

.packs-form {
  display: flex;
  flex-direction: column;
  gap: var(--aha-space-16);
  font-family: var(--aha-font-product);
}

.packs-field {
  display: flex;
  flex-direction: column;
  gap: var(--aha-space-4);
}

.packs-label {
  font-size: var(--aha-size-sm);
  font-weight: 600;
  color: var(--aha-text-secondary);
}

.packs-inline {
  display: flex;
  align-items: flex-start;
  gap: var(--aha-space-8);

  aha-input {
    flex: 1;
  }
}

.packs-summary,
.packs-problems {
  margin: 0;
  padding: 0;
  list-style: none;
}

.packs-problems {
  list-style: disc;
  padding-left: var(--aha-space-16);
}

.packs-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--aha-space-8);
}
</style>
